import DOMUtils from "@utils/dom.utils";
import { createApp } from "vue";
import FillInBlankInput from "@plugins/richtext/plugins/fib-blank/components/FillInBlankInput.vue";
import { isDefined, isEmpty } from "@utils/var.check";
import AppInstanceUtils from "@plugins/common/service/app.instance.utils";
// TODO: Move into fib-blank folder, refactor so that only richtext calls methods from here.
/**
 * Utilities for working with fib input (blanks) elements.
 */
const BlankUtils = {
  /**
   * @description This method returns an array of blank ids according to the sequence in the text content.
   *
   * @param {String} text Contents to extract blanks from
   * @returns {Array} An array of blank ids according to the sequence in the text content
   */
  getBlankIds(text) {
    if (!text) return [];
    const container = DOMUtils.createElement("div");
    container.innerHTML = text;

    const selector = "input.fill-in-box";
    return DOMUtils.elOf(container, selector).map((blank) => {
      return blank.id;
    });
  },

  /**
   * @description This method returns the number of blanks there are in the text content.
   *
   * @param {String} text Content to extract blanks from
   * @returns {Number} The number of blanks there are in the text content
   */
  getBlankCount(text) {
    if (!text) return 0;
    const container = DOMUtils.createElement("div");
    container.innerHTML = text;

    const selector = "input.fill-in-box";
    return DOMUtils.elOf(container, selector).length;
  },

  /**
   * @description This method returns an array of FillInBlankInput blank components that have been mounted into its container.
   *
   * @param {Element} container Container to extract blanks from
   * @param {Object} config Specifies the following:
   *  answerMap: {},
   *  viewMode: VIEW | EDIT | ATTEMPT | RESULT,
   *  optionDisplayMode: TEXTINPUT | SHARED_DROPDOWN | DROPDOWN
   *  options: answer option for dropdown
   *  initialCorrect: boolean,
   *  disabled: boolean,
   *  onChangeFn: Function to be binded to on change.
   * @param parentComponent
   * @returns {Array} An array of mounted FillInBlankInput blank components
   */
  bindFillBlanksInContainer(container, config, parentComponent) {
    const getOptionsData = (options, blankId) => {
      if (isEmpty(options)) return [];
      if (!("id" in options[0])) return options;
      const blankOption = options.find((option) => parseInt(option.id) === parseInt(blankId));
      return isEmpty(blankOption) ? [] : blankOption.option;
    };

    const getGroupData = (blankId) => {
      if (isEmpty(config.groupMap)) {
        return null;
      }

      return config.groupMap[String(blankId)] ?? null;
    };

    const selector = "input.fill-in-box";
    const blanks = DOMUtils.elOf(container, selector);

    const blankComponents = [];
    Array.from(blanks).forEach((blank) => {
      const groupData = getGroupData(blank.id);
      const blankMap = {};
      blankMap.id = blank.id;
      blankMap.value = config.useAnswerMap ? "" : blank.value;
      if (blankMap.value.match(/Answer \d+/g)) {
        blankMap.value = `(${blankMap.value.split(" ")[1]})`;
      }
      blankMap.viewMode = config.viewMode;
      blankMap.correct = config.initialCorrect;
      if (config.useAnswerMap && config.answerMap[blank.id] != null) {
        blankMap.value = config.answerMap[blank.id].answer;
        blankMap.correct = config.answerMap[blank.id].correct;
      }
      blankMap.options = getOptionsData(config.options, blank.id);

      const FillBlankApp = createApp(FillInBlankInput, {
        ...AppInstanceUtils.generateDynamicMountProps(parentComponent),
        value: blankMap.value,
        id: blankMap.id,
        viewMode: blankMap.viewMode,
        optionDisplayMode: config.optionDisplayMode,
        options: blankMap.options,
        correct: blankMap.correct,
        disabled: config.disabled,
        groupColor: groupData?.groupColor,
        groupLabel: groupData?.groupLabel,
        groupNo: groupData?.groupNo ?? null,
        lockRetrieved: config.lockRetrieved,
        onChange: isDefined(config.onChangeFn) && !blankMap.correct ? config.onChangeFn : undefined,
        onFocus: isDefined(config.onChangeFn) && !blankMap.correct ? config.onFocusFn : undefined,
      });
      AppInstanceUtils.installCommonPlugin(FillBlankApp);
      const fillBlank = FillBlankApp.mount(blank);
      DOMUtils.replaceComponentWrapperWithComponentElement(fillBlank);
      blankComponents.push(fillBlank);
    });
    return blankComponents;
  },
};

export default BlankUtils;
